import { BigDecimal } from "@solomon-labs/bigdecimal";
import React from "react";

import { useReportingPayouts } from "./useJachinReporting";
import type { ReportingPayout } from "./useJachinReporting";
import { useReportingLiveApy } from "./useReportingLiveApy";

type Range = "7d" | "30d" | "90d" | "1y" | "all";
const DAYS: Record<Range, number> = { "7d": 7, "30d": 30, "90d": 90, "1y": 365, all: Infinity };
const LABELS: Record<Range, string> = { "7d": "1w", "30d": "1m", "90d": "1q", "1y": "1y", all: "all time" };
interface ReportingApyHistoryResult {
    apyValue: string;
    apyChange: number | null;
    apyPeriodLabel: string;
    chartPoints: number[];
    chartTimestamps: number[];
    chartLabels: string[];
    hasChartData: boolean;
    allTimeApyHistory: { timestamp: number; value: number; exact: string }[];
    pointsByPeriod: { "7d": number; "30d": number; "90d": number; "1y": number; all: number };
    chartStartLabel: string;
    chartEndLabel: string;
}

export function reportingApyHistory(
    payouts: ReportingPayout[],
    range: Range,
    now = Date.now()
): ReportingApyHistoryResult {
    const seen = new Set<string>();
    const points = payouts
        .flatMap((row) => {
            if (seen.has(row.uuid) || row.apyPct === null || !row.paidAt) return [];
            seen.add(row.uuid);
            const timestamp = Date.parse(row.paidAt);
            const value = Number(row.apyPct);
            if (!Number.isFinite(timestamp) || timestamp > now || !Number.isFinite(value)) return [];
            return [{ timestamp, value, exact: row.apyPct }];
        })
        .sort((a, b) => a.timestamp - b.timestamp);
    const inRange = (period: Range): { timestamp: number; value: number; exact: string }[] =>
        points.filter((p) => p.timestamp >= now - DAYS[period] * 86_400_000);
    const selected = inRange(range);
    const first = selected[0];
    const last = selected[selected.length - 1];
    const date = (at: number): string =>
        new Date(at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return {
        apyValue: last ? `${new BigDecimal(last.exact).round(2).toFixed(2)}%` : "—",
        apyChange: first && last ? new BigDecimal(last.exact).subtract(first.exact).toNumber() : null,
        apyPeriodLabel: LABELS[range],
        chartPoints: selected.map((p) => p.value),
        chartTimestamps: selected.map((p) => p.timestamp),
        chartLabels: selected.map((p) => date(p.timestamp)),
        hasChartData: selected.length >= 5,
        allTimeApyHistory: points,
        pointsByPeriod: {
            "7d": inRange("7d").length,
            "30d": inRange("30d").length,
            "90d": inRange("90d").length,
            "1y": inRange("1y").length,
            all: points.length
        },
        chartStartLabel: first ? date(first.timestamp) : "",
        chartEndLabel: last ? date(last.timestamp) : ""
    };
}
interface UseReportingApyHistoryResult {
    liveApy: string;
    isLoading: boolean;
    apyValue: string;
    apyChange: number | null;
    apyPeriodLabel: string;
    chartPoints: number[];
    chartTimestamps: number[];
    chartLabels: string[];
    hasChartData: boolean;
    allTimeApyHistory: { timestamp: number; value: number; exact: string }[];
    pointsByPeriod: { "7d": number; "30d": number; "90d": number; "1y": number; all: number };
    chartStartLabel: string;
    chartEndLabel: string;
}

export function useReportingApyHistory(range: Range): UseReportingApyHistoryResult {
    const query = useReportingPayouts();
    const history = React.useMemo(
        () =>
            reportingApyHistory(
                query.isError || query.data?.status !== "available" ? [] : (query.data.data?.payouts ?? []),
                range
            ),
        [query.data, query.isError, range]
    );
    const { formattedApy } = useReportingLiveApy();
    return { ...history, liveApy: formattedApy, isLoading: query.isLoading };
}
